import type { TFunction } from "i18next";
import { PortalAwardOutcomeDto, type PortalAwardLetterDto } from "../../../../autogen/bff-api";
import { winnerNamesText } from "../../../sourcing-event/award/awardLetterTemplates";

export interface AwardOutcomeMessage {
  title: string;
  body: string | null;
  ranking: string | null;
}

const ownOutcome = (outcome: PortalAwardOutcomeDto, designContest: boolean, t: TFunction): string =>
  outcome === PortalAwardOutcomeDto.Rejected ? (designContest
    ? t("Your entry was rejected.")
    : t("Your bid was rejected.")) : (designContest ? t("Your entry was not among the winners.") : t("Your bid was not selected."));

const winnerBody = (eventRunsPrivateFlow: boolean, t: TFunction): string =>
  eventRunsPrivateFlow
    ? t("You will be notified when the buyer opens for signing the contract.")
    : t("The contract can be signed once the standstill period is over. You will be notified when the buyer opens for signing.");

const contestWinnerBody = (leadsToServiceContract: boolean, t: TFunction): string | null =>
  leadsToServiceContract ? t("The buyer may invite the winners to negotiate a service contract.") : null;

const rankingText = (award: PortalAwardLetterDto, designContest: boolean, t: TFunction): string | null => {
  if (award.outcome !== PortalAwardOutcomeDto.NotSelected || award.myRank == null || award.myRank <= 1) return null;
  if (award.bidderCount == null) return null;
  const rank = award.myRank;
  return designContest
    ? t("You were ranked number {{rank}} of {{count}} evaluated entries.", { rank, count: award.bidderCount })
    : t("You were ranked number {{rank}} of {{count}} evaluated bids.", { rank, count: award.bidderCount });
};

export const awardOutcomeMessage = (
  award: PortalAwardLetterDto,
  eventRunsPrivateFlow: boolean,
  t: TFunction,
  designContest = false,
  leadsToServiceContract = false,
): AwardOutcomeMessage | null => {
  const outcome = award.outcome;
  if (!outcome) return null;

  if (outcome === PortalAwardOutcomeDto.Withdrawn) {
    return {
      title: t("The award has been withdrawn"),
      body: t(
        "The buyer has reversed the award decision. The competition is back in evaluation, and you will be notified when there is a new decision.",
      ),
      ranking: null,
    };
  }

  if (outcome === PortalAwardOutcomeDto.Winner) {
    return designContest
      ? {
          title: t("Your entry is one of the winners of the contest."),
          body: contestWinnerBody(leadsToServiceContract, t),
          ranking: null,
        }
      : { title: t("You won the competition."), body: winnerBody(eventRunsPrivateFlow, t), ranking: null };
  }

  const winner = winnerNamesText(award.winnerNames ?? []);
  const winnerTitle = designContest
    ? t("The contest was won by {{winner}}.", { winner })
    : t("The contract was awarded to {{winner}}.", { winner });
  return {
    title: winner ? winnerTitle : ownOutcome(outcome, designContest, t),
    body: winner ? ownOutcome(outcome, designContest, t) : null,
    ranking: rankingText(award, designContest, t),
  };
};
